// TODO(slava 2026-10): split this file (over the 200-line limit) — move the
// delivery card, its preview grid and the status pill into their own files.

import { Image, PicturePlaceholder } from '../../../modules/design-system';
import { EmptyState } from '../EmptyState';
import type {
  IframePoolShipping,
  PoolShippingStatus,
  PoolShippingSettleSummary,
} from '../../../modules/api';
import { PHOTO_BG_STYLE_DELIVERY } from '../photoBgStyle';

const SKELETON_COUNT = 4;

function ShippingSkeleton() {
  return (
    <div
      data-co-component="MyPacksSkeleton"
      className="aspect-[4/5] w-full rounded-[32px] animate-pulse"
      style={{
        background: 'var(--co-MyPacksSkeleton-fill, rgba(255,255,255,0.05))',
        borderStyle: 'solid',
        borderColor: 'var(--co-MyPacksSkeleton-borderColor, rgba(171,174,179,0.24))',
        borderWidth: 'var(--co-MyPacksSkeleton-borderWidth, 2px)',
      }}
    />
  );
}

export interface DeliveriesTabProps {
  /** Feed state — the pagination hook lives in MyPacksPage (like the
   *  Packs / Cards feeds) so the list survives tab switches and only
   *  skeletons when it's genuinely empty. */
  shippings: IframePoolShipping[];
  /** First-ever load of this feed — the only state that gets a skeleton. A
   *  reload behind an already-rendered tab must not, see useInfinitePagination. */
  initialLoading: boolean;
  loadingMore: boolean;
  reachedEnd: boolean;
  error: string | null;
  /** Error-state "Try again" CTA. */
  onRetry: () => void;
  /** Sentinel ref from useInfinitePagination — attach to a node at the
   *  end of the grid to drive infinite scroll. */
  sentinelRef: (node: Element | null) => void;
  /** Card click → open the Delivery Show page for that shipping id. */
  onOpenDelivery: (shippingId: number) => void;
}

/**
 * Deliveries tab — grid of the user's pool_shippings. Figma 709:2090.
 *
 * Each card has:
 *   • preview area — placeholder grid of pool_item thumbnails (we render
 *     a single placeholder until the API ships per-shipping item previews;
 *     once it does, swap the placeholder loop for the real list).
 *   • info row — N items (big bold), relative date, status pill on the
 *     right (green for shipped/delivered/active, dim grey for canceled,
 *     neutral glass for pending/processing).
 */
export function DeliveriesTab({
  shippings,
  initialLoading,
  loadingMore,
  reachedEnd,
  error,
  onRetry,
  sentinelRef,
  onOpenDelivery,
}: DeliveriesTabProps) {
  if (initialLoading) {
    return (
      <div
        className="
          grid w-full gap-4
          grid-cols-1
          @[600px]:grid-cols-2
          @[900px]:grid-cols-3
          @[1200px]:grid-cols-4
        "
      >
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <ShippingSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error && shippings.length === 0) {
    return (
      <EmptyState
        title="Couldn't load deliveries"
        description={error}
        cta="Try again"
        onAction={onRetry}
      />
    );
  }

  if (shippings.length === 0) {
    return (
      <EmptyState
        title="No deliveries yet"
        description="Ship a card from the Cards tab to start a delivery."
      />
    );
  }

  return (
    <>
      <ul
        className="
          grid w-full gap-4
          grid-cols-1
          @[600px]:grid-cols-2
          @[900px]:grid-cols-3
          @[1200px]:grid-cols-4
        "
      >
        {shippings.map((s) => (
          <li key={s.id}>
            <DeliveryCard
              shipping={s}
              onClick={() => onOpenDelivery(s.id)}
            />
          </li>
        ))}
        {loadingMore &&
          Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <li key={`sk-${i}`}>
              <ShippingSkeleton />
            </li>
          ))}
      </ul>
      {!reachedEnd && <div ref={sentinelRef} aria-hidden className="h-px" />}
    </>
  );
}

const CARD_GLASS_BG =
  'linear-gradient(' +
  'var(--co-MyPacksDeliveryCard-bgDirection, to bottom), ' +
  'color-mix(in srgb, var(--co-MyPacksDeliveryCard-bgFrom, rgba(255,255,255,0.20)) ' +
  'calc(var(--co-MyPacksDeliveryCard-bgOpacity, 1) * 100%), transparent) 0%, ' +
  'color-mix(in srgb, var(--co-MyPacksDeliveryCard-bgTo, rgba(153,153,153,0.20)) ' +
  'calc(var(--co-MyPacksDeliveryCard-bgOpacity, 1) * 100%), transparent) 100%), ' +
  'linear-gradient(90deg, ' +
  'var(--co-MyPacksDeliveryCard-darkTint, rgba(0,0,0,0.18)) 0%, ' +
  'var(--co-MyPacksDeliveryCard-darkTint, rgba(0,0,0,0.18)) 100%)';

interface DeliveryCardProps {
  shipping: IframePoolShipping;
  onClick: () => void;
}

function DeliveryCard({ shipping, onClick }: DeliveryCardProps) {
  const count = shipping.poolItemsCount;
  const itemsLabel = `${count} item${count === 1 ? '' : 's'}`;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open delivery #${shipping.id}`}
      data-co-component="MyPacksDeliveryCard"
      className="
        flex aspect-[4/5] w-full flex-col overflow-hidden
        rounded-[32px] p-2 text-left
        transition-transform active:scale-[0.98]
        hover:opacity-95
      "
      style={{
        backgroundImage: CARD_GLASS_BG,
        backdropFilter: 'blur(var(--co-MyPacksDeliveryCard-glassBlur, 27px))',
        WebkitBackdropFilter: 'blur(var(--co-MyPacksDeliveryCard-glassBlur, 27px))',
        borderStyle: 'solid',
        borderWidth: 'var(--co-MyPacksDeliveryCard-borderWidth, 1px)',
        borderColor: 'var(--co-MyPacksDeliveryCard-borderColor, rgba(171,174,179,0.24))',
        color: 'var(--co-MyPacksDeliveryCard-titleColor, #FFFFFF)',
        fontFamily:
          'var(--co-MyPacksDeliveryCard-fontFamily), Inter, system-ui, sans-serif',
      }}
    >
      <PreviewArea count={count} imageUrl={shipping.previewImageUrl} />
      <div className="flex shrink-0 items-end justify-between gap-3 px-3 pt-3 pb-2">
        <div className="min-w-0">
          <div className="text-[24px] font-bold tracking-[-0.5px]">
            {itemsLabel}
          </div>
          <div
            className="mt-0.5 text-[12px]"
            style={{ color: 'rgba(255,255,255,0.55)' }}
          >
            {formatRelativeDate(shipping.createdAt)}
          </div>
        </div>
        <StatusPill
          status={shipping.status}
          label={shipping.humanizedStatus}
          settleSummary={shipping.settleSummary}
        />
      </div>
    </button>
  );
}

/**
 * Placeholder preview block. Always fills the same flex slot inside the
 * fixed-aspect card (`flex-1 min-h-0`) so every card lines up to the same
 * total height regardless of item count. When `count === 1` we render a
 * single tile; otherwise a 2×2 mini-grid with a "+N" overflow sticker on
 * the last cell. When the API ships a preview image we show it instead.
 */
function PreviewArea({ count, imageUrl }: { count: number; imageUrl: string | null }) {
  if (imageUrl) {
    return (
      <div
        className="relative min-h-0 w-full flex-1 overflow-hidden rounded-[24px]"
        style={PHOTO_BG_STYLE_DELIVERY}
      >
        <Image src={imageUrl} alt="" className="h-full w-full object-cover" />
      </div>
    );
  }
  if (count <= 1) {
    return (
      <div
        className="relative min-h-0 w-full flex-1 overflow-hidden rounded-[24px]"
        style={PHOTO_BG_STYLE_DELIVERY}
      >
        <Placeholder />
      </div>
    );
  }

  // Render up to 4 mini cells; overflow sticker covers extras (≥5 total).
  const cellsToShow = Math.min(count, 4);
  const overflow = count - cellsToShow;
  const tiles = Array.from({ length: cellsToShow });
  return (
    <div
      className="
        grid min-h-0 w-full flex-1 grid-cols-2 grid-rows-2
        gap-2 overflow-hidden rounded-[24px] p-2
      "
      style={PHOTO_BG_STYLE_DELIVERY}
    >
      {tiles.map((_, i) => {
        const isLast = i === cellsToShow - 1;
        return (
          <div key={i} className="relative overflow-hidden rounded-[16px]">
            {isLast && overflow > 0 ? (
              <OverflowSticker remaining={overflow + 1} />
            ) : (
              <Placeholder />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Placeholder() {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{ background: 'rgba(255,255,255,0.04)' }}
    >
      <PicturePlaceholder size={28} strokeWidth={1.5} />
    </div>
  );
}

function OverflowSticker({ remaining }: { remaining: number }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center text-[28px] font-bold"
      style={{
        background: 'rgba(255,255,255,0.06)',
        color: 'rgba(255,255,255,0.85)',
      }}
    >
      {remaining}+
    </div>
  );
}

interface StatusPillProps {
  status: PoolShippingStatus;
  label: string;
  settleSummary?: PoolShippingSettleSummary;
}

// When the warehouse couldn't ship every card, the derived summary takes over
// the pill (amber warning palette, shared with the per-card "Refunded" badge).
const SETTLE_SUMMARY_PILL: Record<'refunded' | 'partially_shipped', string> = {
  refunded: 'Refunded',
  partially_shipped: 'Partially Shipped',
};

function StatusPill({ status, label, settleSummary }: StatusPillProps) {
  if (settleSummary) {
    return (
      <span
        data-co-component="ShippingStatus"
        className="inline-flex h-[28px] shrink-0 items-center rounded-full px-3 text-[12px] font-semibold backdrop-blur-sm"
        style={{
          background: 'var(--co-ShippingStatus-refundedBg, var(--co-ShippingItemStatus-refundedBg, rgba(255,176,32,0.20)))',
          color: 'var(--co-ShippingStatus-refundedFg, var(--co-ShippingItemStatus-refundedFg, #FFB020))',
        }}
      >
        {SETTLE_SUMMARY_PILL[settleSummary]}
      </span>
    );
  }
  const { bg, fg } = statusColors(status);
  return (
    <span
      data-co-component="ShippingStatus"
      className="inline-flex h-[28px] shrink-0 items-center rounded-full px-3 text-[12px] font-semibold"
      style={{ background: bg, color: fg }}
    >
      {label || status}
    </span>
  );
}

// Themed through the ShippingStatus knobs; the fallbacks keep this look.
function statusColors(status: PoolShippingStatus): { bg: string; fg: string } {
  switch (status) {
    case 'shipped':
    case 'delivered':
      return {
        bg: 'var(--co-ShippingStatus-shippedBg, #23D11D)',
        fg: 'var(--co-ShippingStatus-shippedFg, #0E1B12)',
      };
    case 'canceled':
      return {
        bg: 'var(--co-ShippingStatus-canceledBg, rgba(255,255,255,0.06))',
        fg: 'var(--co-ShippingStatus-canceledFg, rgba(255,255,255,0.40))',
      };
    case 'pending':
    default:
      return {
        bg: 'var(--co-ShippingStatus-pendingBg, rgba(255,255,255,0.10))',
        fg: 'var(--co-ShippingStatus-pendingFg, rgba(255,255,255,0.85))',
      };
  }
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function formatRelativeDate(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const diffMs = Date.now() - t;
  const days = Math.floor(diffMs / MS_PER_DAY);
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  return new Date(t).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
